import { type ReactNode } from "react";

export const formInputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-secondary outline-none transition placeholder:text-gray-400 focus:border-secondary/40 focus:bg-white focus:ring-2 focus:ring-secondary/10";

export const formTextareaClass =
  "w-full resize-none rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm text-secondary outline-none transition placeholder:text-gray-400 focus:border-secondary/40 focus:bg-white focus:ring-2 focus:ring-secondary/10";

export const formLabelClass = "text-sm font-semibold text-secondary";

export function FormField({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className={formLabelClass}>
        {label}
        {required ? <span className="ml-0.5 text-primary">*</span> : null}
      </label>
      {children}
      {hint ? <p className="text-xs text-gray-400">{hint}</p> : null}
    </div>
  );
}

export function FormSection({
  title,
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      {title ? (
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-secondary/45">{title}</p>
      ) : null}
      <div className="space-y-3">{children}</div>
    </div>
  );
}

export function FormError({ message }: { message: string }) {
  if (!message) return null;
  return (
    <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700">
      {message}
    </div>
  );
}

export function FormActions({
  onCancel,
  cancelLabel = "Annulla",
  submitLabel,
  loading,
  loadingLabel,
  submitDisabled,
  submitType = "submit",
  onSubmit,
  formId,
}: {
  onCancel: () => void;
  cancelLabel?: string;
  submitLabel: string;
  loading?: boolean;
  loadingLabel?: string;
  submitDisabled?: boolean;
  submitType?: "submit" | "button";
  onSubmit?: () => void;
  formId?: string;
}) {
  return (
    <div className="flex gap-3">
      <button
        type="button"
        onClick={onCancel}
        disabled={loading}
        className="flex-1 rounded-xl border border-slate-200 py-2.5 text-sm font-semibold text-secondary transition hover:bg-slate-50 disabled:opacity-60"
      >
        {cancelLabel}
      </button>
      <button
        type={submitType}
        form={formId}
        onClick={onSubmit}
        disabled={loading || submitDisabled}
        className="flex-1 rounded-xl bg-primary py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-primary/90 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
      >
        {loading ? (loadingLabel ?? "Attendere…") : submitLabel}
      </button>
    </div>
  );
}

type FormModalSize = "sm" | "md" | "lg" | "xl";

const sizeClass: Record<FormModalSize, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-3xl",
};

interface FormModalProps {
  title: string;
  eyebrow?: string;
  description?: string;
  icon?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: FormModalSize;
}

export default function FormModal({
  title,
  eyebrow,
  description,
  icon,
  onClose,
  children,
  footer,
  size = "md",
}: FormModalProps) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1e2d4a]/50 p-5 backdrop-blur-sm">
      <div
        className={`flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-white/20 bg-white shadow-2xl ${sizeClass[size]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="form-modal-title"
      >
        
        <div className="relative shrink-0 overflow-hidden bg-gradient-to-br from-[#2e436a] via-[#3d5280] to-[#1e2d4a] px-6 py-5 text-white">
          <div className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#cd212a]/25 blur-2xl" />
          <div className="relative flex items-start gap-4">
            {icon ? (
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 backdrop-blur-sm">
                {icon}
              </span>
            ) : null}
            <div className="min-w-0 flex-1 pr-8">
              {eyebrow ? (
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/45">{eyebrow}</p>
              ) : null}
              <h2 id="form-modal-title" className="text-xl font-bold tracking-tight">
                {title}
              </h2>
              {description ? <p className="mt-1 text-sm text-white/70">{description}</p> : null}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-white transition hover:bg-white/20"
              aria-label="Chiudi"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>

        {footer ? <div className="shrink-0 border-t border-slate-100 bg-slate-50/50 px-6 py-4">{footer}</div> : null}
      </div>
    </div>
  );
}
